/**
 * GxTable — Table پیشرفتهٔ «مثلِ مرور حساب‌ها»:
 *  • جابه‌جاییِ ستون‌ها با کشیدنِ سرستون (persist per user)
 *  • تغییرِ عرضِ ستون با کشیدنِ لبهٔ سرستون (persist)
 *  • دابل‌کلیک روی لبهٔ تغییرِ عرض = auto-fit به محتوا (مثل اکسل)
 *  • انتخاب/مخفی‌کردنِ ستون‌ها با چک‌باکس (persist)
 *  • جست‌وجو/فیلترِ ستونی (gxColumnFilter)
 *  • خروجیِ CSV/کپی از دادهٔ فیلترشدهٔ فعلی
 *  • نوارِ اسکرولِ افقیِ سفارشی زیرِ جدول — track/thumb همیشه مرئی (بدون fade مک)
 *
 * نکتهٔ هم‌راستایی: sticky هدر به‌صورت پیش‌فرض خاموش است — چون antd هدر و بدنه را
 * دو جدول جدا می‌کند و در RTL سرستون از سلول‌ها جدا می‌شود. با prop sticky قابلِ فعال‌سازی است.
 */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Table, Dropdown, Button, Checkbox, Space, Tooltip, message, Grid } from "antd";
import {
  SettingOutlined, DownloadOutlined, CopyOutlined, ReloadOutlined, ZoomInOutlined, ZoomOutOutlined,
} from "@ant-design/icons";
import {
  loadOrder, saveOrder, autoId, columnsSignature, syncSignature, sanitizeWidth,
} from "../utils/layoutStore";
import { downloadTableXlsx } from "../api/client";
import { GxHeaderCell, measureIntrinsicWidth } from "./gxHeaderCell";
import { enhanceColumnFilter } from "./gxColumnFilter";
import { exportCsv, copyTsv, tableToPayload } from "../utils/tableExport";

/** مرتب‌سازیِ عمومی برای ستون‌های بی‌sorter: عدد با عدد، بولی، بقیه متنِ فارسی؛ خالی‌ها ته. */
function autoSorter(dataIndex) {
  const get = (row) => (Array.isArray(dataIndex)
    ? dataIndex.reduce((o, k) => (o == null ? o : o[k]), row) : row?.[dataIndex]);
  const num = (v) => {
    if (typeof v === "number") return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    if (typeof v === "string" && v.trim() !== "" && /^[-+]?[\d.,]+$/.test(v.trim())) return Number(v.replace(/,/g, ""));
    return null;
  };
  return (a, b) => {
    const va = get(a); const vb = get(b);
    const ea = va == null || va === ""; const eb = vb == null || vb === "";
    if (ea || eb) return ea === eb ? 0 : (ea ? 1 : -1);
    const na = num(va); const nb = num(vb);
    if (na != null && nb != null) return na - nb;
    return String(va).localeCompare(String(vb), "fa");
  };
}
import {
  computeFieldSummary, pageSlice, uniqueSummaryText, faNum,
} from "../utils/gxSummary";

/* v2.133.0 — زومِ لیست مثلِ اکسل (کاربر: «زوم این و زوم اوت… درصد داشته باشیم پایین»).
   برای هر جدول جدا یادش می‌ماند؛ Ctrl/⌘ + چرخِ ماوس روی جدول هم زوم می‌کند. */
const GX_ZOOMS = [50, 60, 70, 80, 90, 100, 110, 125, 150, 175, 200];
const clampZoom = (z) => Math.min(200, Math.max(50, Math.round(Number(z) || 100)));
const stepZoom = (z, dir) => (dir > 0
  ? GX_ZOOMS.find((v) => v > z) ?? 200
  : [...GX_ZOOMS].reverse().find((v) => v < z) ?? 50);

const stableId = (c, i) => String(c.key ?? c.dataIndex ?? `__pos${i}`);
const colTitle = (c) => (typeof c.title === "string" ? c.title : String(c.dataIndex ?? "ستون"));

/** اسکرولِ افقی — نسبت ۰..۱ (سازگار با RTL/antd) */
function scrollRange(el) {
  return Math.max(0, el.scrollWidth - el.clientWidth);
}
function readScrollRatio(el) {
  const range = scrollRange(el);
  if (range <= 0) return 0;
  const sl = el.scrollLeft;
  if (window.getComputedStyle(el).direction === "rtl") {
    if (sl <= 0) return Math.min(1, Math.abs(sl) / range);
    return Math.min(1, sl / range);
  }
  return Math.min(1, sl / range);
}
function writeScrollRatio(el, ratio, rtlMode) {
  const range = scrollRange(el);
  if (range <= 0) return;
  const r = Math.max(0, Math.min(1, ratio));
  if (window.getComputedStyle(el).direction !== "rtl") {
    el.scrollLeft = r * range;
    return;
  }
  el.scrollLeft = rtlMode === "neg" ? -r * range : r * range;
}

function detectRtlScrollMode(el) {
  if (window.getComputedStyle(el).direction !== "rtl") return "ltr";
  const saved = el.scrollLeft;
  el.scrollLeft = -8;
  const mode = el.scrollLeft < 0 ? "neg" : "pos";
  el.scrollLeft = saved;
  return mode;
}

const { useBreakpoint } = Grid;

/** پیش‌فرضِ سراسریِ تعدادِ ردیف در هر صفحه */
export const GX_PAGE_SIZE = 50;   // قانونِ اساسی: پیش‌فرض ۵۰ ردیف، انتخابِ کاربر همه‌جا یادش می‌ماند

/**
 * گزینه‌های «N / صفحه»: یکی کمتر، پیش‌فرض، یکی بیشتر — و اگر فهرست بزرگ‌تر بود
 * پله‌های بعدی هم اضافه می‌شوند تا کاربر مجبور به ورق‌زدنِ بی‌پایان نشود.
 */
export const gxPageSizeOptions = (total = 0, current) => {
  const opts = [10, 25, 50, 100, 200];
  if (total > 200) opts.push(500);
  if (total > 500) opts.push(1000);
  // اندازهٔ فعلی حتماً در فهرست باشد وگرنه antd آن را نشان نمی‌دهد
  if (current && !opts.includes(current)) opts.push(current);
  return opts.sort((a, b) => a - b);
};

export default function GxTable({
  layoutId, columns = [], components, gxSearch = true,
  gxToolbar = true, gxResize = true, gxChooser = true, gxExport = true,
  gxExportName, gxSummary, gxSummaryPosition = "bottom", onChange, dataSource,
  // ستون‌هایی که در حالتِ پیش‌فرض بسته‌اند (جدولِ شلوغ را خلوت می‌کند). کاربر با
  // دکمهٔ «ستون‌ها» بازشان می‌کند و همان انتخاب ذخیره می‌شود.
  gxDefaultHidden,
  /* با عوض‌کردنِ این رشته، چیدمانِ ذخیره‌شدهٔ همین جدول عمداً کهنه می‌شود و
     `gxDefaultHidden`ِ تازه واقعاً اعمال می‌گردد. بدونش، پیش‌فرضِ جدید فقط
     کاربرِ تازه‌وارد را می‌گرفت و کسی که قبلاً یک‌بار ستونی را باز/بسته کرده
     هیچ تغییری نمی‌دید. */
  gxLayoutVersion,
  /* v2.152.12 (کاربر ۱۴۰۵/۰۸/۰۳): «همهٔ لیست‌ها در همهٔ صفحات ستونِ ردیف داشته باشند — ۱، ۲، ۳…». ستونِ شماره خارج از
     چیدمانِ ذخیره‌شده (ستون‌ها/ترتیب/خروجی دست نمی‌خورند)؛ شماره = جایگاه در همان نمای مرتب/فیلترشده (با صفحه‌بندی).
     `gxRowNumbers={false}` یا جدولی که خودش ستونِ «ردیف/#» یا `summary`ِ دستی دارد ⇒ اضافه نمی‌شود. */
  gxRowNumbers = true,
  ...rest
}) {
  /* قانونِ اساسیِ لیست‌ها (v2.112.0): هر GxTable صفحه‌بندیِ مدیریت‌شده دارد — پیش‌فرض ۵۰ ردیف، انتخابِ
     کاربر سراسری یادش می‌ماند — مگر صفحه صراحتاً `pagination={false}` بدهد. 🐞 جدول‌های بدونِ prop
     (مثلِ لیستِ نفراتِ حضور و غیاب) صفحه‌بندیِ پیش‌فرضِ antd را می‌گرفتند: ۱۰ ردیف و بدونِ حافظه. */
  if (rest.pagination === undefined) rest = { ...rest, pagination: {} };
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const ids = useMemo(() => columns.map((c, i) => stableId(c, i)), [columns]);
  const idByCol = useMemo(() => { const m = new Map(); columns.forEach((c, i) => m.set(c, ids[i])); return m; }, [columns, ids]);
  const id = useMemo(() => layoutId || autoId(ids), [layoutId, ids]);
  /* چیدمانِ ذخیره‌شده وقتی ستون‌های جدول عوض شده‌اند باید دور ریخته شود، وگرنه
     به شناسه‌های قدیمی اشاره می‌کند و جدول خراب/خالی رندر می‌شود. */
  const sig = useMemo(() => columnsSignature(ids, gxLayoutVersion), [ids, gxLayoutVersion]);
  const [staleCleared, setStaleCleared] = useState(0);
  useEffect(() => {
    if (syncSignature(id, sig)) setStaleCleared((n) => n + 1);
  }, [id, sig]);
  // چیدمانِ سرور رسید (ورود/بازشدنِ برنامه) ⇒ همین جدول دوباره بخواند
  useEffect(() => {
    const h = () => setStaleCleared((n) => n + 1);
    window.addEventListener("gx-prefs-hydrated", h);
    return () => window.removeEventListener("gx-prefs-hydrated", h);
  }, []);
  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const scrollElRef = useRef(null);
  const rtlModeRef = useRef("ltr");
  const dragRef = useRef(null);

  const [order, setOrder] = useState(() => loadOrder("col", id));
  // فقط بارِ اول از gxDefaultHidden استفاده می‌شود؛ بعد از آن انتخابِ خودِ کاربر
  // (حتی «همه‌چیز باز») حاکم است و با رفرش برنمی‌گردد.
  const [hidden, setHidden] = useState(
    () => new Set(loadOrder("hid", id) || gxDefaultHidden || []));
  /* مرتب‌سازی هم مثلِ ترتیب و عرضِ ستون‌ها یادش می‌ماند. تا امروز تنها چیزی
     بود که با هر بار باز کردنِ صفحه از دست می‌رفت و کاربر باید دوباره روی
     سرستون کلیک می‌کرد. `null` یعنی «کاربر چیزی انتخاب نکرده» — آن وقت
     ستون‌ها دست‌نخورده می‌مانند تا `defaultSortOrder`ِ خودِ صفحه کار کند. */
  const [sort, setSort] = useState(() => loadOrder("srt", id));
  const [widths, setWidths] = useState(() => loadOrder("wid", id) || {});
  const [zoom, setZoomState] = useState(() => clampZoom(loadOrder("zm", id) || 100));
  const zoomRef = useRef(zoom);
  const setZoom = useCallback((z) => {
    const v = clampZoom(typeof z === "function" ? z(zoomRef.current) : z);
    zoomRef.current = v; setZoomState(v); saveOrder("zm", id, v);
  }, [id]);
  const widthsRef = useRef(widths);
  useEffect(() => { widthsRef.current = widths; }, [widths]);
  useEffect(() => {
    setOrder(loadOrder("col", id));
    setSort(loadOrder("srt", id));
    setHidden(new Set(loadOrder("hid", id) || gxDefaultHidden || []));
    setWidths(loadOrder("wid", id) || {});
    { const z = clampZoom(loadOrder("zm", id) || 100); zoomRef.current = z; setZoomState(z); }
    setPageInfo({ current: 1, pageSize: loadOrder("pgs", "__global__") || GX_PAGE_SIZE });
  }, [id, staleCleared]);

  const [viewData, setViewData] = useState(dataSource);
  /* پیش‌فرضِ ۱۰۰ ردیف در صفحه (خواستهٔ کاربر: ۵۰ / ۱۰۰ / ۲۰۰).
     قبلاً ۱۰ بود و برای فهرست‌های واقعیِ این کارخانه خیلی کوچک بود. */
  const [pageInfo, setPageInfo] = useState(
    () => ({ current: 1, pageSize: loadOrder("pgs", "__global__") || GX_PAGE_SIZE }));
  useEffect(() => { setViewData(dataSource); }, [dataSource]);
  const handleChange = useCallback((p, f, sorterArg, extra) => {
    if (extra && extra.currentDataSource) setViewData(extra.currentDataSource);
    if (p && typeof p === "object") {
      // اندازهٔ انتخابیِ کاربر برای همین جدول یادش می‌ماند
      if (p.pageSize && p.pageSize !== pageInfo.pageSize) saveOrder("pgs", "__global__", p.pageSize);
      setPageInfo({
        current: p.current || 1,
        pageSize: p.pageSize || pageInfo.pageSize || GX_PAGE_SIZE,
      });
    }
    /* 🔴 فقط وقتی کاربر **واقعاً روی سرستون زده**.
       antd با هر تعاملی (حتی عوض‌کردنِ صفحه یا فیلتر) مرتب‌سازیِ جاری را پس
       می‌دهد — از جمله `defaultSortOrder`ِ خودِ صفحه. اگر همان را ذخیره کنیم،
       کاربری که فقط صفحهٔ ۲ را زده ناخواسته یک مرتب‌سازیِ «انتخاب‌شده» پیدا
       می‌کند و از آن به بعد پیش‌فرضِ خودِ صفحه هرگز اعمال نمی‌شود.
       حالتِ چندستونه آرایه می‌آید و همان‌طور نگه داشته می‌شود. */
    if (sorterArg !== undefined && extra && extra.action === "sort") {
      const list = (Array.isArray(sorterArg) ? sorterArg : [sorterArg])
        .filter((x) => x && x.order)
        .map((x) => ({ key: String(x.columnKey ?? x.field ?? ""), order: x.order }))
        .filter((x) => x.key);
      const next = list.length ? list : null;
      saveOrder("srt", id, next);
      setSort(next);
    }
    if (typeof onChange === "function") onChange(p, f, sorterArg, extra);
  }, [onChange, pageInfo.pageSize, id]);

  const ordered = useMemo(() => {
    if (!order || !order.length) return columns;
    const byId = new Map(columns.map((c, i) => [ids[i], c]));
    const used = new Set();
    const out = [];
    order.forEach((oid) => { if (byId.has(oid)) { out.push(byId.get(oid)); used.add(oid); } });
    columns.forEach((c, i) => { if (!used.has(ids[i])) out.push(c); });
    return out;
  }, [columns, ids, order]);

  const move = useCallback((from, to) => {
    const cur = ordered.map((c) => idByCol.get(c));
    const fi = cur.indexOf(from), ti = cur.indexOf(to);
    if (fi < 0 || ti < 0) return;
    cur.splice(ti, 0, cur.splice(fi, 1)[0]);
    saveOrder("col", id, cur); setOrder(cur);
  }, [ordered, idByCol, id]);

  const resize = useCallback((colId, w, commit) => {
    if (commit) { saveOrder("wid", id, widthsRef.current); return; }
    // کف/سقف: ستونِ نزدیکِ صفر محتوایش را روی ستونِ بغلی می‌ریزد و کاربر
    // فکر می‌کند عددها قاطی شده‌اند.
    const safe = sanitizeWidth(w);
    if (safe == null) return;
    setWidths((prev) => { const next = { ...prev, [colId]: safe }; widthsRef.current = next; return next; });
  }, [id]);

  // دابل‌کلیک روی لبه = اندازهٔ خودکار بر اساس دادهٔ سلول‌ها (نه عنوانِ بلند)
  const autoFit = useCallback((colId) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const headers = Array.from(wrap.querySelectorAll(".ant-table-thead th"))
      .filter((th) => !th.classList.contains("ant-table-selection-column")
        && !th.classList.contains("ant-table-cell-scrollbar")
        && !th.classList.contains("ant-table-row-expand-icon-cell"));
    const colIdx = headers.findIndex((th) => th.getAttribute("data-gx-id") === colId);
    if (colIdx < 0) return;

    let maxBody = 0;
    const bump = (el, into) => {
      const w = measureIntrinsicWidth(el);
      return Math.max(into, w);
    };
    // اولویت با دادهٔ بدنه؛ عنوان فقط کفِ کمینه می‌سازد (نه سقفِ بزرگ)
    const rows = wrap.querySelectorAll(".ant-table-tbody > tr");
    const limit = Math.min(rows.length, 80);
    for (let i = 0; i < limit; i++) {
      const cells = Array.from(rows[i].children).filter((td) =>
        !td.classList.contains("ant-table-selection-column")
        && !td.classList.contains("ant-table-cell-scrollbar")
        && !td.classList.contains("ant-table-row-expand-icon-cell"));
      maxBody = bump(cells[colIdx], maxBody);
    }
    // عنوان: فقط متنِ کوتاهِ عنوان را بسنج (بدون آیکن فیلتر/سورت که عرض را باد می‌کند)
    const th = headers[colIdx];
    let titleMin = 48;
    if (th) {
      const titleEl = th.querySelector(".ant-table-column-title") || th;
      const probe = document.createElement("span");
      probe.textContent = (titleEl.textContent || "").trim().slice(0, 24);
      probe.style.cssText = "position:absolute;visibility:hidden;white-space:nowrap;font:inherit";
      document.body.appendChild(probe);
      titleMin = Math.ceil(probe.getBoundingClientRect().width) + 28; // فضای آیکن فیلتر
      document.body.removeChild(probe);
    }
    const contentW = maxBody > 0 ? maxBody : titleMin;
    // عنوان Wrap می‌شود؛ عرض را بیشتر از داده باد نمی‌کنیم (حداکثر +۱۲٪ نسبت به داده)
    const nextW = Math.min(420, Math.max(56, Math.max(contentW + 8, Math.min(titleMin, Math.round(contentW * 1.15) + 8))));
    setWidths((prev) => {
      const next = { ...prev, [colId]: nextW };
      widthsRef.current = next;
      saveOrder("wid", id, next);
      return next;
    });
  }, [id]);

  const toggleHidden = useCallback((colId, show) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (show) next.delete(colId); else next.add(colId);
      saveOrder("hid", id, Array.from(next));
      return next;
    });
  }, [id]);

  const visibleCols = useMemo(() => ordered.filter((c) => !hidden.has(idByCol.get(c))), [ordered, hidden, idByCol]);
  /* نگاشتِ «کلیدِ ستون → جهتِ مرتب‌سازی». کلید همان چیزی است که antd در
     `columnKey ?? field` پس می‌دهد، پس با `key ?? dataIndex` تطبیق می‌خورد. */
  const sortByKey = useMemo(() => {
    if (!sort) return null;                 // هیچ‌وقت چیزی ذخیره نشده
    const m = new Map();                    // `[]` ⇒ Map خالی = «کنترل‌شده، بدونِ ترتیب»
    sort.forEach((x, i) => m.set(x.key, { order: x.order, multiple: sort.length - i }));
    return m;
  }, [sort]);

  const cols = useMemo(() => visibleCols.map((c0) => {
    const cid = idByCol.get(c0);
    // v2.132.0 (کاربر): «همهٔ لیست‌ها بر اساسِ همهٔ ستون‌ها قابلِ مرتب‌سازی باشند» — ستونِ داده‌دارِ بی‌sorter خودکار
    // مرتب‌سازیِ عمومی می‌گیرد (عدد عددی، متن الفبای فارسی). `gxSort: false` برای ستون‌هایی که نباید.
    const c = (!c0.sorter && c0.dataIndex != null && c0.gxSort !== false && !c0.children)
      ? { ...c0, sorter: autoSorter(c0.dataIndex) } : c0;
    const withFilter = gxSearch ? enhanceColumnFilter(c, viewData || dataSource) : c;
    const w = isMobile ? undefined : widths[cid];
    /* ⚠️ فقط وقتی چیزی ذخیره شده باشد `sortOrder` ست می‌شود. اگر همیشه ست
       می‌شد، جدول «کنترل‌شده» می‌ماند و `defaultSortOrder`ِ صفحه‌ها بی‌اثر
       می‌گشت — یعنی همان چیزی که قرار بود درست شود، خراب می‌شد. */
    const sortKey = String(c.key ?? c.dataIndex ?? "");
    const saved = sortByKey && c.sorter ? sortByKey.get(sortKey) : null;
    return {
      ...withFilter,
      ...(sortByKey && c.sorter ? { sortOrder: saved ? saved.order : null } : {}),
      ...(w ? { width: w } : {}),
      onHeaderCell: (orig) => {
        const base = typeof c.onHeaderCell === "function" ? c.onHeaderCell(orig) : {};
        return {
          ...base,
          gxId: cid,
          gxMove: move,
          gxResize: gxResize && !isMobile ? resize : undefined,
          gxAutoFit: gxResize && !isMobile ? autoFit : undefined,
        };
      },
    };
  }), [visibleCols, idByCol, move, resize, autoFit, gxResize, gxSearch, viewData, dataSource, widths, isMobile, sortByKey]);

  const dataCols = useMemo(() => ordered.filter((c) => c.dataIndex != null), [ordered]);
  const exportCols = useMemo(() => visibleCols.filter((c) => c.dataIndex != null), [visibleCols]);

  const chooserMenu = {
    items: [
      ...dataCols.map((c) => {
        const cid = idByCol.get(c);
        return {
          key: cid,
          label: (
            <Checkbox checked={!hidden.has(cid)} onClick={(e) => e.stopPropagation()}
              onChange={(e) => toggleHidden(cid, e.target.checked)}>
              {colTitle(c)}
            </Checkbox>
          ),
        };
      }),
      { type: "divider" },
      { key: "__reset", label: <span><ReloadOutlined /> نمایشِ همه</span> },
      /* راهِ خروج از عرض‌های خراب؛ بدونِ این، ستونی که یک‌بار خیلی باریک شده
         تا ابد باریک می‌ماند و کاربر فکر می‌کند دادهٔ جدول خراب است. */
      ...(Object.keys(widths).length
        ? [{ key: "__unwidth", label: <span><ReloadOutlined /> بازنشانیِ عرضِ ستون‌ها</span> }]
        : []),
      /* مرتب‌سازی حالا ماندگار است، پس باید راهی برای برداشتنش باشد — وگرنه
         کاربری که یک‌بار روی سرستون کلیک کرده تا ابد همان ترتیب را می‌بیند
         و فکر می‌کند دادهٔ جدول عوض شده. */
      ...((sort && sort.length)
        ? [{ key: "__unsort", label: <span><ReloadOutlined /> برداشتنِ مرتب‌سازی</span> }]
        : []),
    ],
    onClick: ({ key }) => {
      if (key === "__reset") { setHidden(new Set()); saveOrder("hid", id, []); }
      /* 🔴 `null` کافی نیست: با null دیگر هیچ `sortOrder`ی به antd نمی‌دهیم و
         antd به حالتِ **کنترل‌نشدهٔ داخلیِ خودش** برمی‌گردد — که هنوز همان
         مرتب‌سازی را دارد. پس جدول تا رفرشِ صفحه مرتب می‌ماند. `[]` یعنی
         «کنترل‌شده ولی بدونِ ترتیب» و واقعاً پاکش می‌کند. */
      if (key === "__unsort") { setSort([]); saveOrder("srt", id, []); }
      if (key === "__unwidth") { setWidths({}); widthsRef.current = {}; saveOrder("wid", id, {}); }
    },
  };

  /* خروجیِ پیش‌فرض **اکسلِ قالب‌دار** است، نه CSV.
     CSV نه عرضِ ستون دارد نه قالب، و شماره‌های بلند (شبا/کارت) را به نماد
     علمی می‌برد. ساختِ فایل سمتِ سرور است تا همهٔ خروجی‌های سیستم یک شکل
     باشند؛ ولی ستون‌ها و ردیف‌ها از **همین صفحه** می‌روند، پس خروجی دقیقاً
     همان چیزی است که کاربر می‌بیند (فیلترشده و به ترتیبِ فعلی). */
  const [exporting, setExporting] = useState(false);
  const exportTitle = gxExportName || layoutId || "خروجی";
  const doExport = async () => {
    setExporting(true);
    try {
      const payload = tableToPayload(exportCols, viewData || dataSource || [], exportTitle);
      payload.subtitle = `${payload.rows.length} ردیف — ${new Date().toLocaleDateString("fa-IR")}`;
      await downloadTableXlsx(payload, `${exportTitle}.xlsx`);
      message.success("فایلِ اکسل دانلود شد");
    } catch {
      // اگر سرور در دسترس نبود، دستِ کاربر خالی نماند
      const name = `${exportTitle}-${new Date().toISOString().slice(0, 10)}.csv`;
      exportCsv(exportCols, viewData || dataSource || [], name);
      message.warning("اکسل ساخته نشد — فایلِ CSV دانلود شد");
    }
    setExporting(false);
  };
  const doCopy = async () => {
    const ok = await copyTsv(exportCols, viewData || dataSource || []);
    ok ? message.success("در کلیپ‌بورد کپی شد (چسباندن در Excel)") : message.error("کپی نشد");
  };

  const merged = { ...components, header: { ...(components && components.header), cell: GxHeaderCell } };

  const wide = !!(rest.scroll && rest.scroll.x);

  // سرستونِ چسبان — با APIِ خودِ antd، نه CSS دستی.
  //
  // ⚠️ تلاشِ قبلی (v2.58.0) با `position:sticky` روی خودِ `th` جدول‌ها را شکست:
  // بدنهٔ جدول `overflow-x:auto` دارد، پس ظرفِ چسبندگی **همان بدنه** می‌شود نه
  // صفحه، و سرستون ۶۴ پیکسل به پایین می‌افتاد روی ردیف‌ها.
  // `sticky` خودِ antd هدر را در یک لایهٔ جدا و نسبت به **صفحه** می‌چسباند، پس
  // آن مشکل را ندارد. تنها ریسکش ناهم‌گامیِ افقی در RTL است که `syncScroll`ِ
  // همین کامپوننت از قبل `.ant-table-header` را هم هم‌گام می‌کند.
  const TOPBAR = isMobile ? 56 : 64;
  /* 🐞 v2.112.0 — صفحه‌ای که نوارِ چسبانِ خودش را دارد (نوارِ مراحلِ حضور و غیاب) سرستون را زیرِ آن نوار
     می‌برد و «اسمِ ستون‌ها با پایین‌رفتن ناپدید می‌شد». هر نوارِ چسبانِ صفحه کلاسِ `gx-sticky-bar` می‌گیرد
     و سرستون دقیقاً زیرِ آن‌ها می‌نشیند. */
  const [barH, setBarH] = useState(0);
  useEffect(() => {
    const measure = () => {
      const h = [...document.querySelectorAll(".gx-sticky-bar")]
        .reduce((a, el) => a + el.getBoundingClientRect().height, 0);
      setBarH((p) => (Math.abs(p - h) < 1 ? p : Math.round(h)));
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    document.querySelectorAll(".gx-sticky-bar").forEach((el) => ro?.observe(el));
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 300);
    return () => { ro?.disconnect(); window.removeEventListener("resize", measure); clearTimeout(t); };
  }, []);
  const sticky = rest.sticky !== undefined
    ? rest.sticky
    : { offsetHeader: TOPBAR + barH };

  // نوارِ اسکرولِ سفارشی — track/thumb همیشه مرئی (اسکرول‌بارِ overlay مک fade می‌شود)
  const [hs, setHs] = useState({ sw: 0, cw: 0, ratio: 0 });
  const [trackW, setTrackW] = useState(0);
  const wantBar = wide && !isMobile;

  const findScrollEl = useCallback((wrap) => {
    if (!wrap) return null;
    const candidates = [
      wrap.querySelector(".ant-table-body"),
      wrap.querySelector(".ant-table-content"),
      wrap.querySelector(".ant-table-header"),
    ].filter(Boolean);
    let best = candidates[0] || null;
    let bestOver = -1;
    candidates.forEach((el) => {
      const over = el.scrollWidth - el.clientWidth;
      if (over > bestOver) { bestOver = over; best = el; }
    });
    return best;
  }, []);

  /* 🐞 v2.109.0 — «هدر با اسکرولِ چپ و راست به‌هم می‌ریزد و با لگ جابه‌جا می‌شود»:
     ۱) هر رویدادِ اسکرول `setHs` می‌زد ⇒ کلِ جدول (همهٔ ردیف‌ها) هر فریم دوباره رندر می‌شد = لگ.
     ۲) هدر و بدنه دوطرفه هم‌گام می‌شدند و گاردِ `syncing` بی‌اثر بود (رویدادِ اسکرول ناهمگام است)،
        پس هدر و بدنه هم‌دیگر را پس‌وپیش می‌کشیدند = لرزش و جابه‌جاییِ سرستون.
     حالا: آخرین موقعیت در ref نگه داشته می‌شود و رویدادی که همان موقعیت (±۱px) را دارد نادیده گرفته
     می‌شود؛ نوارِ سفارشی مستقیم در DOM جابه‌جا می‌شود، بدونِ رندر. */
  const lastLeftRef = useRef(null);
  const thumbRef = useRef(null);
  const thumbGeomRef = useRef({ trackW: 0, thumbW: 48 });
  const rafRef = useRef(0);
  const syncScroll = useCallback((left) => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    lastLeftRef.current = left;
    [wrap.querySelector(".ant-table-body"),
      wrap.querySelector(".ant-table-content"),
      wrap.querySelector(".ant-table-header"),
    ].filter(Boolean).forEach((el) => {
      if (Math.abs(el.scrollLeft - left) >= 1) el.scrollLeft = left;
    });
  }, []);
  const placeThumb = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const sc = scrollElRef.current;
      const th = thumbRef.current;
      if (!sc || !th) return;
      const { trackW: tw, thumbW: w } = thumbGeomRef.current;
      th.style.insetInlineStart = `${tw > w ? Math.round(readScrollRatio(sc) * (tw - w)) : 0}px`;
    });
  }, []);
  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  /* هم‌گامیِ سرستون و بدنه — یک شنوندهٔ capture روی ظرفِ بیرونی (نه روی خودِ بدنه).
     🐞 شنونده قبلاً مستقیم به `.ant-table-body` وصل می‌شد، ولی antd بعد از رندرِ اول همان
     عنصر را از نو می‌سازد ⇒ شنونده روی عنصرِ جداشده می‌ماند و هرگز صدا نمی‌خورد؛ فقط
     هم‌گامیِ خودِ antd می‌ماند که بعد از اولین حرکت از کار می‌افتاد و سرستون تا ۲۰۰۰px از
     سلول‌ها جا می‌ماند. رویدادِ scroll حباب نمی‌زند ولی در فازِ capture به والد می‌رسد. */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    const onScroll = (e) => {
      const el = e.target;
      if (!el || !el.classList) return;
      if (!(el.classList.contains("ant-table-body") || el.classList.contains("ant-table-header")
            || el.classList.contains("ant-table-content"))) return;
      if (!el.closest || el.closest(".ant-table-wrapper") !== wrap.querySelector(".ant-table-wrapper")) return;
      const left = el.scrollLeft;
      if (lastLeftRef.current != null && Math.abs(left - lastLeftRef.current) < 1) return;
      if (el.classList.contains("ant-table-body") || el.classList.contains("ant-table-content")) {
        scrollElRef.current = el;
      }
      syncScroll(left);
      placeThumb();
    };
    wrap.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => wrap.removeEventListener("scroll", onScroll, { capture: true });
  }, [syncScroll, placeThumb]);

  const applyRatio = useCallback((ratio) => {
    const sc = scrollElRef.current;
    if (!sc) return;
    writeScrollRatio(sc, ratio, rtlModeRef.current);
    syncScroll(sc.scrollLeft);
    placeThumb();
  }, [syncScroll, placeThumb]);

  useEffect(() => {
    if (!wantBar) return undefined;
    const wrap = wrapRef.current;
    if (!wrap) return undefined;
    let sc = findScrollEl(wrap);
    if (!sc) return undefined;
    scrollElRef.current = sc;
    rtlModeRef.current = detectRtlScrollMode(sc);
    const measure = () => {
      sc = findScrollEl(wrap) || sc;
      if (!sc) return;
      scrollElRef.current = sc;
      setHs((p) => {
        const next = {
          sw: sc.scrollWidth, cw: sc.clientWidth,
          ratio: readScrollRatio(sc),
        };
        return (p.sw === next.sw && p.cw === next.cw && p.ratio === next.ratio) ? p : next;
      });
    };
    measure();
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 280);
    const t3 = setTimeout(measure, 800);
    const ro = new ResizeObserver(measure);
    ro.observe(wrap);
    if (sc) ro.observe(sc);
    const innerTbl = sc?.querySelector("table");
    if (innerTbl) ro.observe(innerTbl);
    // 🐞 با سرستونِ چسبان، antd هدر و بدنه را دو ظرفِ اسکرولِ **جدا** می‌کند.
    // این شنونده فقط نوارِ سفارشی را به‌روز می‌کرد، پس با اسکرولِ افقی هدر سرِ
    // جایش می‌ماند و ستون‌ها از سلول‌ها می‌افتادند (مشکلِ کلاسیکِ RTL/antd).
    // حالا موقعیتِ افقی هم آینه می‌شود.
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      ro.disconnect();
      window.removeEventListener("resize", measure);
      scrollElRef.current = null;
    };
  }, [wantBar, dataSource, cols, findScrollEl, syncScroll, placeThumb]);

  const hasOverflow = hs.sw > hs.cw + 1;
  const showBar = wantBar && hasOverflow;

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !showBar) return undefined;
    const ro = new ResizeObserver(() => setTrackW(track.clientWidth));
    ro.observe(track);
    setTrackW(track.clientWidth);
    return () => ro.disconnect();
  }, [showBar, hs.sw, hs.cw]);

  const onTrackClick = useCallback((e) => {
    if (e.button !== 0 || e.target.classList.contains("gx-hscroll-thumb")) return;
    const track = trackRef.current;
    const sc = scrollElRef.current;
    if (!track || !sc) return;
    const rect = track.getBoundingClientRect();
    const tw = Math.max(48, trackW > 0 && hs.sw > 0 ? (hs.cw / hs.sw) * trackW : 48);
    const travel = Math.max(1, track.clientWidth - tw);
    const rtl = window.getComputedStyle(track).direction === "rtl";
    const pos = rtl ? rect.right - e.clientX : e.clientX - rect.left;
    applyRatio(Math.max(0, Math.min(1, (pos - tw / 2) / travel)));
  }, [applyRatio, trackW, hs.sw, hs.cw]);

  const onThumbDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const sc = scrollElRef.current;
    const track = trackRef.current;
    if (!sc || !track) return;
    const startX = e.clientX;
    const startRatio = readScrollRatio(sc);
    const tw = Math.max(48, trackW > 0 && hs.sw > 0 ? (hs.cw / hs.sw) * trackW : 48);
    const travel = Math.max(1, track.clientWidth - tw);
    dragRef.current = { startX, startRatio, travel, rtl: window.getComputedStyle(track).direction === "rtl" };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      let delta = (ev.clientX - d.startX) / d.travel;
      if (d.rtl) delta = -delta;
      applyRatio(d.startRatio + delta);
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [applyRatio, trackW, hs.sw, hs.cw]);

  const thumbW = showBar && trackW > 0 && hs.sw > 0
    ? Math.max(48, Math.round((hs.cw / hs.sw) * trackW))
    : 48;
  const thumbOffset = showBar && trackW > thumbW
    ? Math.round(hs.ratio * (trackW - thumbW))
    : 0;
  thumbGeomRef.current = { trackW, thumbW };

  const toolbar = gxToolbar && (gxChooser || gxExport) ? (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginBottom: 6 }}>
      <Space size={4}>
        {gxChooser && (
          <Dropdown menu={chooserMenu} trigger={["click"]} placement="bottomRight">
            <Tooltip title="ستون‌ها را انتخاب کنید">
              <Button size="small" icon={<SettingOutlined />}>ستون‌ها</Button>
            </Tooltip>
          </Dropdown>
        )}
        {gxExport && (
          <Dropdown trigger={["click"]} placement="bottomRight" menu={{
            items: [
              { key: "csv", icon: <DownloadOutlined />, label: "خروجی اکسل (xlsx)" },
              { key: "copy", icon: <CopyOutlined />, label: "کپی برای Excel" },
            ],
            onClick: ({ key }) => (key === "csv" ? doExport() : doCopy()),
          }}>
            <Button size="small" icon={<DownloadOutlined />} loading={exporting}>خروجی</Button>
          </Dropdown>
        )}
      </Space>
    </div>
  ) : null;

  /* pageSizeِ پیش‌فرض فقط وقتی از prop خوانده می‌شود که **مقدارش** عوض شده باشد.
     قبلاً وابستگیِ افکت خودِ شیءِ pagination بود و چون صفحه‌ها آن را inline
     می‌سازند (`pagination={{ pageSize: 25 }}`)، هر رندر شیءِ تازه بود و انتخابِ
     کاربر بلافاصله به مقدارِ پیش‌فرض برمی‌گشت. */
  /* `pageSize`ِ صفحه‌ها فقط وقتی اعمال می‌شود که صراحتاً `gxFixedPageSize`
     بدهند. وگرنه اعدادِ پراکندهٔ قدیمی (۸، ۱۲، ۱۵، ۲۰، ۲۵…) دوباره جدول‌ها را
     کوچک می‌کردند، در حالی که قرار است همه‌جا یکسان و ۱۰۰ باشد. */
  const propPageSize =
    rest.pagination && typeof rest.pagination === "object" && rest.pagination.gxFixedPageSize
      ? rest.pagination.pageSize : undefined;
  useEffect(() => {
    if (propPageSize) {
      setPageInfo((p) => (p.pageSize === propPageSize ? p : { ...p, pageSize: propPageSize }));
    }
  }, [propPageSize]);

  const summarySpecs = useMemo(() => {
    if (!gxSummary) return null;
    if (Array.isArray(gxSummary)) {
      const m = new Map();
      gxSummary.forEach((s) => { if (s && s.dataIndex != null) m.set(String(s.dataIndex), s); });
      return m;
    }
    return null;
  }, [gxSummary]);

  const hasPagination = !!(rest.pagination && rest.pagination !== false);
  // ستونِ «ردیف» (v2.152.12) — فقط وقتی جدول خودش شماره ندارد و جمعِ دستی (`summary`) هم ندارد (ترازِ خانه‌های جمع)
  const rowNumbers = gxRowNumbers && !rest.summary && !columns.some((c) => {
    const t = typeof c.title === "string" ? c.title.trim() : "";
    return t === "ردیف" || t === "#" || c.key === "__row" || c.dataIndex === "row_number";
  });
  const rowNoCol = useMemo(() => ({
    key: "__gxrow", title: "ردیف", width: 56, align: "center",
    fixed: cols[0]?.fixed || undefined,
    render: (_v, _r, i) => (
      <span className="gx-num" style={{ color: "var(--text-mute, #999)", fontSize: 12 }}>
        {faNum((hasPagination ? (Math.max(1, Number(pageInfo.current) || 1) - 1) * (Number(pageInfo.pageSize) || GX_PAGE_SIZE) : 0) + i + 1)}
      </span>),
  }), [cols, hasPagination, pageInfo]);
  // تعدادِ کلِ ردیف‌ها — هم برای گزینه‌های صفحه‌بندی، هم برای نمایشِ جمع
  const rowCount = Array.isArray(dataSource) ? dataSource.length : 0;

  const renderSummary = useCallback(() => {
    if (!summarySpecs || !summarySpecs.size) return null;
    const filtered = viewData || dataSource || [];
    const pageRows = hasPagination
      ? pageSlice(filtered, { current: pageInfo.current, pageSize: pageInfo.pageSize })
      : filtered;
    const showSplit = hasPagination && filtered.length > pageRows.length;
    const selOffset = (rest.rowSelection ? 1 : 0) + (rowNumbers ? 1 : 0);

    const cellText = (spec, rows, di) => {
      if (!spec || spec.type === "none") return null;
      const fmt = spec.format || ((v) => faNum(v));
      if (spec.type === "unique") return uniqueSummaryText(rows, di);
      const val = computeFieldSummary(rows, spec);
      return val == null ? "—" : fmt(val);
    };

    const buildRow = (scope, rows, label) => {
      const cells = [];
      if (rest.rowSelection) {
        cells.push(
          <Table.Summary.Cell key={`${scope}-sel`} index={0}>
            <span className="gx-summary-label">{label}</span>
          </Table.Summary.Cell>,
        );
      }
      if (rowNumbers) {
        cells.push(<Table.Summary.Cell key={`${scope}-rowno`} index={rest.rowSelection ? 1 : 0} />);
      }
      cols.forEach((col, i) => {
        const idx = i + selOffset;
        const di = col.dataIndex;
        const spec = di != null ? summarySpecs.get(String(di)) : null;
        const isFirstLabel = i === 0 && !rest.rowSelection;
        if (isFirstLabel && (!spec || spec.type === "none")) {
          cells.push(
            <Table.Summary.Cell key={`${scope}-${col.key || di || i}`} index={idx}>
              <span className="gx-summary-label">{label}</span>
            </Table.Summary.Cell>,
          );
          return;
        }
        if (!spec || spec.type === "none") {
          cells.push(<Table.Summary.Cell key={`${scope}-${col.key || di || i}`} index={idx} />);
          return;
        }
        const txt = cellText(spec, rows, di);
        cells.push(
          <Table.Summary.Cell key={`${scope}-${col.key || di || i}`} index={idx} align={col.align}>
            {isFirstLabel ? (
              <span>
                <span className="gx-summary-label" style={{ marginInlineEnd: 8 }}>{label}</span>
                <b className="gx-num">{txt}</b>
              </span>
            ) : (
              <b className="gx-num">{txt}</b>
            )}
          </Table.Summary.Cell>,
        );
      });
      return cells;
    };

    return (
      <Table.Summary fixed={gxSummaryPosition === "top" ? "top" : "bottom"}>
        {showSplit && (
          <Table.Summary.Row className="gx-summary-row gx-summary-row-page">
            {buildRow("page", pageRows, "جمع صفحه")}
          </Table.Summary.Row>
        )}
        <Table.Summary.Row className={`gx-summary-row gx-summary-row-total${showSplit ? " gx-summary-row-split" : ""}`}>
          {buildRow("total", filtered, showSplit ? "جمع کل" : "جمع")}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [summarySpecs, viewData, dataSource, hasPagination, pageInfo, cols, rest.rowSelection, gxSummaryPosition, rowNumbers]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      setZoom((z) => stepZoom(z, e.deltaY < 0 ? 1 : -1));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [setZoom]);

  // sticky و summary از rest جدا شوند تا override نشوند
  const { summary: restSummary, sticky: _stickyIgnored, ...tableRest } = rest;

  /* انتخاب‌گرِ «N / صفحه» کار نمی‌کرد: صفحه‌ها `pagination={{ pageSize: 25 }}`
     می‌دادند و همین pageSizeِ ثابت، جدول را کنترل‌شده می‌کرد — antd انتخابِ کاربر
     را می‌گرفت ولی رندرِ بعدی دوباره به همان عدد برمی‌گشت. حالا اندازهٔ انتخابیِ
     کاربر (pageInfo) روی مقدارِ ثابت سوار می‌شود و صفحهٔ جاری هم کنترل می‌شود. */
  if (tableRest.pagination && typeof tableRest.pagination === "object") {
    const { gxFixedPageSize: _gxFixed, ...pagRest } = tableRest.pagination;
    tableRest.pagination = {
      showSizeChanger: true,
      pageSizeOptions: gxPageSizeOptions(rowCount, pageInfo.pageSize),
      // تعدادِ کل همیشه تهِ فهرست دیده شود — «این لیست چند تاست؟»
      showTotal: (total, range) =>
        `${faNum(range[0])}–${faNum(range[1])} از ${faNum(total)} ردیف`,
      ...pagRest,
      current: Number(pageInfo.current) > 0 ? pageInfo.current : 1,
      // سپرِ دوم: هر مقدارِ خرابی اینجا به عددِ معتبر برمی‌گردد؛ pageSizeِ
      // نامعتبر یعنی جدولِ کاملاً خالی، بدترین شکستِ ممکن برای کاربر.
      pageSize: Number(pageInfo.pageSize) > 0 ? Number(pageInfo.pageSize) : GX_PAGE_SIZE,
    };
  }

  return (
    <div ref={wrapRef} className={showBar ? "gx-wide-table" : undefined}>
      {toolbar}
      <div className="gx-zoom-area" style={zoom !== 100 ? { zoom: zoom / 100 } : undefined}>
      <Table
        columns={rowNumbers ? [rowNoCol, ...cols] : cols}
        components={merged}
        dataSource={dataSource}
        onChange={handleChange}
        summary={summarySpecs ? renderSummary : restSummary}
        {...tableRest}
        sticky={sticky}
      />
      </div>
      <div className="gx-table-foot" style={{ display: "flex", alignItems: "center", gap: 8,
        justifyContent: "space-between", padding: "4px 8px", fontSize: 12 }}>
        {/* جدولِ بدونِ صفحه‌بندی هم باید بگوید چند ردیف است — همان چیزی که
            صفحه‌بندی با showTotal نشان می‌دهد. */}
        <span style={{ opacity: 0.7 }}>
          {!hasPagination && rowCount > 0 ? `مجموع: ${faNum(rowCount)} ردیف` : ""}
        </span>
        {!isMobile && (
          <Space size={2} className="gx-zoom" aria-label="بزرگ‌نمایی جدول">
            <Tooltip title="کوچک‌تر (Ctrl + چرخِ ماوس)">
              <Button size="small" type="text" icon={<ZoomOutOutlined />} disabled={zoom <= 50}
                onClick={() => setZoom((z) => stepZoom(z, -1))} />
            </Tooltip>
            <input type="range" min={50} max={200} step={5} value={zoom}
              onChange={(e) => setZoom(e.target.value)} style={{ width: 90, direction: "ltr" }}
              aria-label="درصدِ بزرگ‌نمایی" />
            <Tooltip title="بزرگ‌تر (Ctrl + چرخِ ماوس)">
              <Button size="small" type="text" icon={<ZoomInOutlined />} disabled={zoom >= 200}
                onClick={() => setZoom((z) => stepZoom(z, 1))} />
            </Tooltip>
            <Tooltip title="بازگشت به ۱۰۰٪">
              <Button size="small" type="text" onClick={() => setZoom(100)}
                style={{ minWidth: 44, fontVariantNumeric: "tabular-nums" }}>{faNum(zoom)}٪</Button>
            </Tooltip>
          </Space>
        )}
      </div>
      {showBar && (
        <div className="gx-hscroll" aria-hidden="true">
          <div
            className="gx-hscroll-track"
            ref={trackRef}
            onMouseDown={onTrackClick}
            role="presentation"
          >
            <div
              className="gx-hscroll-thumb"
              ref={thumbRef}
              style={{ width: thumbW, insetInlineStart: thumbOffset }}
              onMouseDown={onThumbDown}
              role="presentation"
            />
          </div>
        </div>
      )}
    </div>
  );
}
